// Component: PullToRefresh.jsx
// Purpose: Native-feeling pull-to-refresh for phone scroll surfaces. When the
//   wrapped scrollable is at the top and the user drags DOWN, the content
//   rubber-bands down and a refresh indicator is revealed above it: the DS
//   Loader's candlesticks (LoaderGlyph), BUILDING with the pull. As the drag
//   progresses (0 → threshold) the candles rise in a stagger, high-contrast
//   (var(--fg-1)). The instant the release threshold is armed the whole glyph
//   tints teal (var(--color-profit)) as the "release to refresh" cue. Past
//   threshold + release → the built candles hand off to the Loader's own
//   candles-only "heartbeat" loop (same geometry, resting at full height so
//   there's no collapse/pop) while onRefresh's promise pends, then the content
//   springs back (SPRING_SMOOTH). Released below threshold → springs back.
//
//   Scroll safety: the gesture only engages when the nearest scrollable
//   ancestor (or the document, when the phone shell owns scrolling) is at
//   scrollTop 0 AND the drag is downward — otherwise normal scrolling is never
//   touched. We only preventDefault once we've committed to a pull, so vertical
//   scroll and horizontal gestures pass through untouched. touchmove is bound
//   non-passive so that preventDefault is honoured.
//
//   Reduced-motion → the glyph snaps to its fully-built static form (no per-
//   candle build, no loop); the spring-backs collapse to instant.
//
// Usage:
//   <PullToRefresh onRefresh={async () => refetch()}>
//     <ScreenContent/>
//   </PullToRefresh>

import React, { useEffect, useRef, useState } from "react";
import { motion, animate, useMotionValue, useTransform, useReducedMotion } from "framer-motion";
import { cx } from "./cx";
import { LoaderGlyph } from "./Loader";
import { SPRING_SMOOTH } from "./motion";

const THRESHOLD = 72; // px of resisted pull needed to arm a refresh
const REST = 56; // px the content holds at while the refresh promise pends
const RESIST = 0.55; // drag→translate ratio (rubber-band feel)
const MAX_PULL = THRESHOLD * 1.6; // soft cap so it can't be dragged to the moon
const GLYPH_PX = 30; // LoaderGlyph height → ~43px-wide box in the indicator

// The indicator subscribes to the RAW pull motion value `y` (not the derived
// `progress` transform) and re-renders on change to feed LoaderGlyph a plain 0..1
// number. Subscribing to `y` matters: a useTransform re-syncs (and emits) during
// its owner's render, which would setState mid-render; a plain useMotionValue
// only changes in the gesture handlers / spring, never during render. Isolated in
// its own component so only the tiny glyph re-renders per move — never the
// scrolled content tree.
function PtrIndicator({ y, indicatorOpacity, refreshing }) {
  const toProgress = (v) => Math.min(Math.max(v / THRESHOLD, 0), 1);
  const [p, setP] = useState(() => toProgress(y.get()));
  useEffect(() => {
    setP(toProgress(y.get()));
    return y.on("change", (v) => setP(toProgress(v)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [y]);
  return (
    <motion.div className="ds-ptr-indicator" style={{ opacity: indicatorOpacity }} aria-hidden="true">
      {refreshing ? (
        <LoaderGlyph size={GLYPH_PX} />
      ) : (
        <LoaderGlyph size={GLYPH_PX} tone="mono" progress={p} />
      )}
    </motion.div>
  );
}

// Nearest scrollable ancestor, or null when the document/window owns scrolling
// (the phone AppShell case). Walks up from the wrapper.
function findScroller(el) {
  let node = el?.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const oy = getComputedStyle(node).overflowY;
    if ((oy === "auto" || oy === "scroll") && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return null;
}

function atTop(scroller) {
  if (scroller) return scroller.scrollTop <= 0;
  const doc = document.documentElement;
  return (window.scrollY || doc.scrollTop || 0) <= 0;
}

export function PullToRefresh({ onRefresh, children, disabled = false, className, style, ...props }) {
  const reduced = useReducedMotion();
  const wrapRef = useRef(null);
  const y = useMotionValue(0);
  const [refreshing, setRefreshing] = useState(false);
  const refreshingRef = useRef(false);

  // Indicator reveal is derived from the live pull (0 → threshold): the glyph
  // build is driven from `y` inside PtrIndicator; `indicatorOpacity` fades the
  // strip in as the pull begins.
  const indicatorOpacity = useTransform(y, [0, THRESHOLD * 0.35, THRESHOLD], [0, 0.55, 1], { clamp: true });

  // Promote the content to its own GPU layer ONLY while a pull is live (y != 0).
  // A permanent will-change: transform kept it layered at rest, where a fractional
  // pixel offset left a 1px hairline at its top edge — visible in light mode and
  // on scroll. Off at rest → no layer → no seam; on during the pull → smooth.
  const willChange = useTransform(y, (v) => (v !== 0 ? "transform" : "auto"));

  // Clip the wrapper ONLY while a pull is live, for the same reason as above: the
  // clip exists to contain content translated by the pull, and a pull is touch-only
  // (this component binds touchstart/move/end and nothing else). A PERMANENT
  // overflow: hidden therefore did nothing at rest except slice the top off any
  // child that paints outside its box — the wrapper's top edge is flush with its
  // content (measured: 0px clearance), so a card lifting on hover
  // (.card-hover:hover → translateY(-4px) + shadow-xl) lost its top edge and its
  // entire top shadow. Visible at rest → the lift paints; hidden during the pull →
  // the translated content stays contained, exactly as before.
  const overflow = useTransform(y, (v) => (v !== 0 ? "hidden" : "visible"));

  // Mutable gesture state (kept off React state to avoid per-move re-renders).
  // `decided` locks the axis once the finger has moved enough: a vertical-down
  // drag becomes a pull; anything else (horizontal swipe → SwipeRow, upward)
  // is handed back so we never hijack those gestures.
  const g = useRef({ dragging: false, decided: false, startY: 0, startX: 0, scroller: null });
  const AXIS_LOCK = 6; // px of travel before we commit to a direction

  const springBack = () => {
    if (reduced) y.set(0);
    else animate(y, 0, SPRING_SMOOTH);
  };

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;

    const onStart = (e) => {
      if (disabled || refreshingRef.current || e.touches.length !== 1) {
        g.current.dragging = false;
        return;
      }
      const scroller = findScroller(el);
      if (!atTop(scroller)) {
        g.current.dragging = false;
        return;
      }
      g.current = {
        dragging: true,
        decided: false,
        startY: e.touches[0].clientY,
        startX: e.touches[0].clientX,
        scroller,
      };
    };

    const onMove = (e) => {
      const s = g.current;
      if (!s.dragging) return;
      const dy = e.touches[0].clientY - s.startY;
      const dx = e.touches[0].clientX - s.startX;

      // Axis lock: wait for a little travel, then commit. Only a downward,
      // vertical-dominant drag becomes a pull; otherwise release the gesture
      // (horizontal → SwipeRow, upward → scroll) and never touch it again.
      if (!s.decided) {
        if (Math.abs(dx) < AXIS_LOCK && Math.abs(dy) < AXIS_LOCK) return;
        if (dy > 0 && dy >= Math.abs(dx)) {
          s.decided = true;
        } else {
          s.dragging = false;
          y.set(0);
          return;
        }
      }

      if (dy <= 0) {
        // Pulled back above the origin → collapse; let scrolling resume.
        y.set(0);
        return;
      }
      // If the surface scrolled away from the top mid-gesture, bail out.
      if (!atTop(s.scroller)) {
        s.dragging = false;
        y.set(0);
        return;
      }
      e.preventDefault(); // we own this downward-at-top drag now
      y.set(Math.min(dy * RESIST, MAX_PULL));
    };

    const onEnd = () => {
      const s = g.current;
      if (!s.dragging) return;
      s.dragging = false;
      const pulled = y.get();
      if (pulled >= THRESHOLD && !refreshingRef.current) {
        refreshingRef.current = true;
        setRefreshing(true);
        if (reduced) y.set(REST);
        else animate(y, REST, SPRING_SMOOTH);
        Promise.resolve()
          .then(() => onRefresh?.())
          .finally(() => {
            refreshingRef.current = false;
            setRefreshing(false);
            springBack();
          });
      } else {
        springBack();
      }
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd, { passive: true });
    el.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, reduced, onRefresh]);

  // `style` is pulled out of props and merged below so a caller-supplied style
  // cannot silently drop the transient overflow — the clip is this component's own
  // invariant, so it wins.
  return (
    <motion.div ref={wrapRef} className={cx("ds-ptr", className)} style={{ ...style, overflow }} {...props}>
      {/* The market glyph builds with the pull (LoaderGlyph, progress-driven).
          On release past threshold it swaps to the Loader's own looping form —
          same geometry, same box → no jump. */}
      <PtrIndicator y={y} indicatorOpacity={indicatorOpacity} refreshing={refreshing} />

      <motion.div className="ds-ptr-content" style={{ y, willChange }}>
        {children}
      </motion.div>
    </motion.div>
  );
}

export default PullToRefresh;
